import React, { useState, useRef, useEffect } from 'react'
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  FormControlLabel,
  Checkbox,
  InputLabel,
  Paper,
  Divider,
  Grid,
  AppBar,
  Toolbar,
  Stack,
  Card,
  CardHeader,
  CardContent,
  ListItem,
  List,
  ListItemText,
  Tooltip
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import MenuIcon from '@mui/icons-material/Menu'
import PrintIcon from '@mui/icons-material/Print'

const LabelDesignerDragDrop = () => {
  const [labelElements, setLabelElements] = useState([
    {
      id: 'product-name',
      type: 'text',
      content: 'Product Name',
      x: 10,
      y: 10,
      width: 150,
      height: 20,
      fontSize: 14,
      fontWeight: 'bold'
    },
    {
      id: 'lot-number',
      type: 'text',
      content: 'Lot Number',
      x: 10,
      y: 30,
      width: 150,
      height: 20,
      fontSize: 12
    },
    {
      id: 'barcode',
      type: 'barcode',
      content: '9780201379624',
      x: 170,
      y: 10,
      width: 200,
      height: 40
    },
    {
      id: 'id-label',
      type: 'text',
      content: 'ID:',
      x: 10,
      y: 60,
      width: 80,
      height: 20,
      fontSize: 12,
      fontWeight: 'bold'
    },
    {
      id: 'container-label',
      type: 'text',
      content: 'Container:',
      x: 10,
      y: 80,
      width: 80,
      height: 20,
      fontSize: 12,
      fontWeight: 'bold'
    },
    {
      id: 'condition-label',
      type: 'text',
      content: 'Condition:',
      x: 10,
      y: 100,
      width: 80,
      height: 20,
      fontSize: 12,
      fontWeight: 'bold'
    },
    {
      id: 'date-label',
      type: 'text',
      content: 'Create Date:',
      x: 10,
      y: 120,
      width: 80,
      height: 20,
      fontSize: 12,
      fontWeight: 'bold'
    },
    {
      id: 'date-value',
      type: 'text',
      content: '01/01/10',
      x: 90,
      y: 120,
      width: 80,
      height: 20,
      fontSize: 12
    }
  ])

  // Available tools/fields that can be dragged
  const availableTools = [
    { id: 'text-field', label: 'Text', type: 'text', content: 'New Text' },
    {
      id: 'barcode-field',
      label: 'BarCode',
      type: 'barcode',
      content: '123456789012'
    },
    {
      id: 'product-name-field',
      label: 'Product Name',
      type: 'text',
      content: 'Product Name'
    },
    { id: 'lot-field', label: 'Lot', type: 'text', content: 'Lot Number' },
    { id: 'id-field', label: 'ID', type: 'text', content: 'ID' },
    {
      id: 'container-field',
      label: 'Container',
      type: 'text',
      content: 'Container'
    },
    { id: 'date-field', label: 'Initial Date', type: 'text', content: 'Date' }
  ]

  const [draggedTool, setDraggedTool] = useState(null)
  const [selectedElement, setSelectedElement] = useState(null)
  const [isDragging, setIsDragging] = useState(false)
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 })

  // Add state for scrollbars, rulers, and zoom
  const [showScrollbars, setShowScrollbars] = useState(true)
  const [showRulers, setShowRulers] = useState(true)
  const [zoomLevel, setZoomLevel] = useState('100')

  const canvasRef = useRef(null)
  const canvasContainerRef = useRef(null)

  // Handle starting to drag a tool from the sidebar
  const handleToolDragStart = tool => {
    setDraggedTool(tool)
  }

  // Handle dropping a new tool onto the canvas
  const handleCanvasDrop = e => {
    e.preventDefault()

    if (draggedTool) {
      const canvasRect = canvasRef.current.getBoundingClientRect()
      // Adjust for zoom level
      const zoomFactor = parseFloat(zoomLevel) / 100
      const x = (e.clientX - canvasRect.left) / zoomFactor
      const y = (e.clientY - canvasRect.top) / zoomFactor

      const newElement = {
        id: `${draggedTool.id}-${Date.now()}`,
        type: draggedTool.type,
        content: draggedTool.content,
        x: x,
        y: y,
        width: draggedTool.type === 'barcode' ? 200 : 100,
        height: draggedTool.type === 'barcode' ? 40 : 20,
        fontSize: 12
      }

      setLabelElements([...labelElements, newElement])
      setDraggedTool(null)
    }
  }

  // Handle dragover event
  const handleCanvasDragOver = e => {
    e.preventDefault()
  }

  // Handle clicking on an element in the canvas
  const handleElementClick = (e, element) => {
    e.stopPropagation()
    // Find the current state of the element to ensure we're working with the latest data
    const currentElement = labelElements.find(el => el.id === element.id)
    setSelectedElement(currentElement)
  }

  // Handle starting to drag an existing element
  const handleElementDragStart = (e, element) => {
    e.stopPropagation()

    const zoomFactor = parseFloat(zoomLevel) / 100
    const elementRect = e.currentTarget.getBoundingClientRect()
    setDragOffset({
      x: (e.clientX - elementRect.left) / zoomFactor,
      y: (e.clientY - elementRect.top) / zoomFactor
    })

    setSelectedElement(element)
    setIsDragging(true)
  }

  // Handle dragging an element
  const handleCanvasMouseMove = e => {
    if (isDragging && selectedElement) {
      e.preventDefault()

      const zoomFactor = parseFloat(zoomLevel) / 100
      const canvasRect = canvasRef.current.getBoundingClientRect()
      const x = (e.clientX - canvasRect.left) / zoomFactor - dragOffset.x
      const y = (e.clientY - canvasRect.top) / zoomFactor - dragOffset.y

      setLabelElements(
        labelElements.map(el => (el.id === selectedElement.id ? { ...el, x, y } : el))
      )

      // Also update the selected element to reflect the new position
      setSelectedElement({ ...selectedElement, x, y })
    }
  }

  // Handle dropping an element after dragging
  const handleCanvasMouseUp = () => {
    setIsDragging(false)
  }

  // Handle clicking on empty canvas
  const handleCanvasClick = () => {
    setSelectedElement(null)
  }

  // Handle deleting a selected element
  const handleDeleteElement = () => {
    if (selectedElement) {
      setLabelElements(labelElements.filter(el => el.id !== selectedElement.id))
      setSelectedElement(null)
    }
  }

  // Handle toggling scrollbars
  const handleScrollbarsToggle = e => {
    setShowScrollbars(e.target.checked)
  }

  // Handle toggling rulers
  const handleRulersToggle = e => {
    setShowRulers(e.target.checked)
  }

  // Handle changing zoom level
  const handleZoomChange = e => {
    setZoomLevel(e.target.value)
  }

  // Render a barcode visualization
  const renderBarcode = (content, width, height) => {
    const bars = []
    for (let i = 0; i < 60; i++) {
      const value = content.charCodeAt(i % content.length) % 10
      const barWidth = value > 5 ? 2 : 1
      bars.push(
        <rect key={i} x={i * 3} y={0} width={barWidth} height={height * 0.8} fill="black" />
      )
    }

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <svg width={width} height={height * 0.8} viewBox={`0 0 180 ${height * 0.8}`}>
          {bars}
        </svg>
        <Typography variant="caption" sx={{ mt: 0.5, textAlign: 'center' }}>
          {content}
        </Typography>
      </Box>
    )
  }

  // State for canvas size
  const [canvasSize, setCanvasSize] = useState({ width: 500, height: 300 })

  // Calculate scaled canvas size based on zoom level
  const zoomFactor = parseFloat(zoomLevel) / 100
  const scaledCanvasSize = {
    width: canvasSize.width * zoomFactor,
    height: canvasSize.height * zoomFactor
  }

  // Handle printing the canvas
  const handlePrint = () => {
    const printWindow = window.open('', '_blank')
    if (!printWindow) {
      alert('Please allow pop-ups to print the label.')
      return
    }

    // Get the canvas content
    const canvasEl = canvasRef.current
    if (!canvasEl) return

    // Create print-friendly HTML
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Label Print</title>
          <style>
            body {
              margin: 0;
              padding: 20px;
              font-family: Arial, sans-serif;
            }
            .print-container {
              position: relative;
              width: ${canvasSize.width}px;
              height: ${canvasSize.height}px;
              border: 1px solid #ccc;
              box-sizing: border-box;
              background-color: white;
              margin: 0 auto;
            }
            .print-element {
              position: absolute;
              box-sizing: border-box;
            }
            .barcode-container {
              display: flex;
              flex-direction: column;
              align-items: center;
            }
            .barcode-number {
              font-size: 10px;
              margin-top: 3px;
              text-align: center;
            }
            @media print {
              body {
                padding: 0;
              }
              .print-header, .print-footer {
                display: none;
              }
              .print-container {
                border: none;
              }
            }
          </style>
        </head>
        <body>
          <div class="print-header">
            <h2 style="text-align: center;">Label Preview</h2>
            <div style="text-align: center; margin-bottom: 20px;">
              <button onclick="window.print()">Print Label</button>
              <button onclick="window.close()">Close</button>
            </div>
          </div>
          
          <div class="print-container">
            ${generatePrintableElements()}
          </div>
          
          <div class="print-footer">
            <div style="text-align: center; margin-top: 20px;">
              <button onclick="window.print()">Print Label</button>
              <button onclick="window.close()">Close</button>
            </div>
          </div>
        </body>
      </html>
    `)

    printWindow.document.close()
  }

  // Generate HTML for printable elements
  const generatePrintableElements = () => {
    return labelElements
      .map(element => {
        if (element.type === 'text') {
          return `
          <div class="print-element" style="
            left: ${element.x}px;
            top: ${element.y}px;
            width: ${element.width}px;
            height: ${element.height}px;
            font-size: ${element.fontSize}px;
            font-weight: ${element.fontWeight || 'normal'};
          ">
            ${element.content}
          </div>
        `
        } else if (element.type === 'barcode') {
          // Generate simple barcode for print
          let barcodeHtml = `
          <div class="print-element barcode-container" style="
            left: ${element.x}px;
            top: ${element.y}px;
            width: ${element.width}px;
            height: ${element.height}px;
          ">
            <svg width="${element.width}" height="${element.height * 0.8}" viewBox="0 0 180 ${element.height * 0.8}">
        `

          // Generate bars
          for (let i = 0; i < 60; i++) {
            const value = element.content.charCodeAt(i % element.content.length) % 10
            const barWidth = value > 5 ? 2 : 1
            barcodeHtml += `<rect x="${i * 3}" y="0" width="${barWidth}" height="${element.height * 0.8}" fill="black" />`
          }

          barcodeHtml += `
            </svg>
            <div class="barcode-number">${element.content}</div>
          </div>
        `

          return barcodeHtml
        }
        return ''
      })
      .join('')
  }

  // Generate ruler tick marks
  const generateRulerTicks = (isHorizontal, length) => {
    // Use 100px intervals for both rulers
    const tickInterval = 100
    // Calculate number of ticks based on the actual canvas dimensions
    const tickCount = Math.floor(length / tickInterval)

    // Create an array of ticks plus intermediate minor ticks
    const ticks = []

    for (let i = 0; i <= tickCount; i++) {
      // Position in pixels, adjusted for zoom
      const position = i * (tickInterval * zoomFactor)

      // Major tick (every 100px)
      ticks.push(
        isHorizontal ? (
          <Box
            key={`major-${i}`}
            sx={{
              position: 'absolute',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              left: position
            }}
          >
            <Box sx={{ height: '12px', width: '1px', bgcolor: 'grey.600' }}></Box>
          </Box>
        ) : (
          <Box
            key={`major-${i}`}
            sx={{
              position: 'absolute',
              top: position,
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end'
            }}
          >
            <Box sx={{ width: '12px', height: '1px', bgcolor: 'grey.600' }}></Box>
          </Box>
        )
      )

      // Add minor ticks (every 50px) except after the last major tick
      if (i < tickCount) {
        const minorPosition = (i * tickInterval + 50) * zoomFactor
        ticks.push(
          isHorizontal ? (
            <Box
              key={`minor-${i}`}
              sx={{
                position: 'absolute',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                left: minorPosition
              }}
            >
              <Box sx={{ height: '8px', width: '1px', bgcolor: 'grey.400' }}></Box>
            </Box>
          ) : (
            <Box
              key={`minor-${i}`}
              sx={{
                position: 'absolute',
                top: minorPosition,
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end'
              }}
            >
              <Box sx={{ width: '8px', height: '1px', bgcolor: 'grey.400' }}></Box>
            </Box>
          )
        )
      }
    }

    return ticks
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: 'grey.100' }}>
      {/* Top toolbar */}
      <AppBar
        position="static"
        color="default"
        elevation={0}
        sx={{ borderBottom: 1, borderColor: 'divider' }}
      >
        <Toolbar variant="dense">
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
            <Typography variant="body2" sx={{ mr: 1 }}>
              Name
            </Typography>
            <TextField
              defaultValue="Avery 5262"
              size="small"
              variant="outlined"
              sx={{ width: '120px' }}
            />
          </Box>

          <IconButton size="small" sx={{ mx: 1, bgcolor: 'grey.300' }}>
            <MenuIcon fontSize="small" />
          </IconButton>

          <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
            <Typography variant="body2" sx={{ mr: 1 }}>
              Zoom:
            </Typography>
            <FormControl size="small" variant="outlined" sx={{ width: '80px' }}>
              <Select value={zoomLevel} onChange={handleZoomChange}>
                <MenuItem value="150">150%</MenuItem>
                <MenuItem value="100">100%</MenuItem>
                <MenuItem value="75">75%</MenuItem>
                <MenuItem value="50">50%</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
            <FormControlLabel
              control={
                <Checkbox size="small" checked={showScrollbars} onChange={handleScrollbarsToggle} />
              }
              label={<Typography variant="body2">Scrollbars</Typography>}
              sx={{ mr: 2 }}
            />

            <FormControlLabel
              control={<Checkbox size="small" checked={showRulers} onChange={handleRulersToggle} />}
              label={<Typography variant="body2">Rulers</Typography>}
            />
          </Box>

          <Box sx={{ ml: 'auto', display: 'flex', gap: 1, alignItems: 'center' }}>
            <Tooltip title="Print Label">
              <IconButton
                color="primary"
                onClick={handlePrint}
                size="small"
                sx={{ border: '1px solid', borderColor: 'primary.main', mr: 1 }}
              >
                <PrintIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Typography variant="body2" sx={{ mr: 2 }}>
              Print
            </Typography>

            {selectedElement && (
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleDeleteElement}
              >
                Delete Element
              </Button>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Main content */}
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Left panel - Tools */}
        <Paper
          elevation={0}
          square
          sx={{
            width: '200px',
            bgcolor: 'grey.100',
            borderRight: 1,
            borderColor: 'divider',
            overflow: 'auto'
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              py: 1,
              px: 2,
              bgcolor: 'grey.200',
              borderBottom: 1,
              borderColor: 'divider'
            }}
          >
            Tools
          </Typography>
          <List disablePadding>
            {availableTools.map(tool => (
              <ListItem
                key={tool.id}
                button
                sx={{
                  py: 1,
                  borderBottom: 1,
                  borderColor: 'divider',
                  '&:hover': { bgcolor: 'primary.50' }
                }}
                draggable
                onDragStart={() => handleToolDragStart(tool)}
              >
                <ListItemText primary={tool.label} primaryTypographyProps={{ variant: 'body2' }} />
              </ListItem>
            ))}
          </List>
        </Paper>

        {/* Center panel - Canvas */}
        <Box
          sx={{
            flex: 1,
            bgcolor: 'grey.200',
            p: 2,
            overflow: showScrollbars ? 'auto' : 'hidden'
          }}
          onMouseMove={handleCanvasMouseMove}
          onMouseUp={handleCanvasMouseUp}
          onMouseLeave={handleCanvasMouseUp}
          ref={canvasContainerRef}
        >
          {/* Horizontal ruler */}
          {showRulers && (
            <Box
              sx={{
                position: 'relative',
                height: '24px',
                ml: '24px',
                mb: 0.5,
                width: scaledCanvasSize.width,
                bgcolor: 'common.white',
                borderBottom: 1,
                borderColor: 'grey.300',
                overflow: 'hidden'
              }}
            >
              {generateRulerTicks(true, canvasSize.width)}
            </Box>
          )}

          <Box sx={{ display: 'flex' }}>
            {/* Vertical ruler */}
            {showRulers && (
              <Box
                sx={{
                  width: '24px',
                  height: scaledCanvasSize.height,
                  bgcolor: 'common.white',
                  borderRight: 1,
                  borderColor: 'grey.300',
                  overflow: 'hidden',
                  position: 'relative'
                }}
              >
                {generateRulerTicks(false, canvasSize.height)}
              </Box>
            )}

            {/* Canvas */}
            <Box
              ref={canvasRef}
              sx={{
                width: `${scaledCanvasSize.width}px`,
                height: `${scaledCanvasSize.height}px`,
                border: 2,
                borderColor: 'grey.500',
                bgcolor: 'common.white',
                position: 'relative',
                transformOrigin: '0 0'
              }}
              onClick={handleCanvasClick}
              onDrop={handleCanvasDrop}
              onDragOver={handleCanvasDragOver}
            >
              {labelElements.map(element => (
                <Box
                  key={element.id}
                  sx={{
                    position: 'absolute',
                    left: `${element.x * zoomFactor}px`,
                    top: `${element.y * zoomFactor}px`,
                    width: `${element.width * zoomFactor}px`,
                    height: `${element.height * zoomFactor}px`,
                    transform: `scale(${zoomFactor})`,
                    transformOrigin: 'top left',
                    cursor: 'move',
                    outline: selectedElement?.id === element.id ? '2px solid' : 'none',
                    outlineColor: 'primary.main'
                  }}
                  onClick={e => handleElementClick(e, element)}
                  onMouseDown={e => handleElementDragStart(e, element)}
                >
                  {element.type === 'text' ? (
                    <Typography
                      sx={{
                        fontSize: `${element.fontSize}px`,
                        fontWeight: element.fontWeight || 'normal'
                      }}
                    >
                      {element.content}
                    </Typography>
                  ) : element.type === 'barcode' ? (
                    renderBarcode(element.content, element.width, element.height)
                  ) : null}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        {/* Right panel - Properties */}
        <Paper
          elevation={0}
          square
          sx={{
            width: '240px',
            bgcolor: 'grey.100',
            borderLeft: 1,
            borderColor: 'divider',
            overflow: 'auto'
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              py: 1,
              px: 2,
              bgcolor: 'grey.200',
              borderBottom: 1,
              borderColor: 'divider'
            }}
          >
            Properties
          </Typography>

          {/* Canvas size controls */}
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Canvas Size
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label="Width"
                  type="number"
                  size="small"
                  fullWidth
                  value={canvasSize.width}
                  onChange={e =>
                    setCanvasSize({
                      ...canvasSize,
                      width: Number(e.target.value)
                    })
                  }
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Height"
                  type="number"
                  size="small"
                  fullWidth
                  value={canvasSize.height}
                  onChange={e =>
                    setCanvasSize({
                      ...canvasSize,
                      height: Number(e.target.value)
                    })
                  }
                />
              </Grid>
            </Grid>
          </Box>

          {selectedElement ? (
            <Box sx={{ p: 2 }}>
              <TextField
                label="Content"
                fullWidth
                size="small"
                value={selectedElement.content}
                onChange={e => {
                  const updatedElements = labelElements.map(el =>
                    el.id === selectedElement.id ? { ...el, content: e.target.value } : el
                  )
                  setLabelElements(updatedElements)
                  setSelectedElement({
                    ...selectedElement,
                    content: e.target.value
                  })
                }}
                sx={{ mb: 2 }}
              />

              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Position
              </Typography>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                  <TextField
                    label="X"
                    type="number"
                    size="small"
                    fullWidth
                    value={Math.round(selectedElement.x)}
                    onChange={e => {
                      const newValue = Number(e.target.value)
                      setLabelElements(
                        labelElements.map(el =>
                          el.id === selectedElement.id ? { ...el, x: newValue } : el
                        )
                      )
                      setSelectedElement({ ...selectedElement, x: newValue })
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Y"
                    type="number"
                    size="small"
                    fullWidth
                    value={Math.round(selectedElement.y)}
                    onChange={e => {
                      const newValue = Number(e.target.value)
                      setLabelElements(
                        labelElements.map(el =>
                          el.id === selectedElement.id ? { ...el, y: newValue } : el
                        )
                      )
                      setSelectedElement({ ...selectedElement, y: newValue })
                    }}
                  />
                </Grid>
              </Grid>

              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Size
              </Typography>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                  <TextField
                    label="Width"
                    type="number"
                    size="small"
                    fullWidth
                    value={selectedElement.width}
                    onChange={e => {
                      const newValue = Number(e.target.value)
                      setLabelElements(
                        labelElements.map(el =>
                          el.id === selectedElement.id ? { ...el, width: newValue } : el
                        )
                      )
                      setSelectedElement({
                        ...selectedElement,
                        width: newValue
                      })
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Height"
                    type="number"
                    size="small"
                    fullWidth
                    value={selectedElement.height}
                    onChange={e => {
                      const newValue = Number(e.target.value)
                      setLabelElements(
                        labelElements.map(el =>
                          el.id === selectedElement.id ? { ...el, height: newValue } : el
                        )
                      )
                      setSelectedElement({
                        ...selectedElement,
                        height: newValue
                      })
                    }}
                  />
                </Grid>
              </Grid>

              {selectedElement.type === 'text' && (
                <>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Text Style
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <TextField
                        label="Font Size"
                        type="number"
                        size="small"
                        fullWidth
                        value={selectedElement.fontSize}
                        onChange={e => {
                          const newValue = Number(e.target.value)
                          setLabelElements(
                            labelElements.map(el =>
                              el.id === selectedElement.id ? { ...el, fontSize: newValue } : el
                            )
                          )
                          setSelectedElement({
                            ...selectedElement,
                            fontSize: newValue
                          })
                        }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Weight</InputLabel>
                        <Select
                          value={selectedElement.fontWeight || 'normal'}
                          label="Weight"
                          onChange={e => {
                            const newValue = e.target.value
                            setLabelElements(
                              labelElements.map(el =>
                                el.id === selectedElement.id ? { ...el, fontWeight: newValue } : el
                              )
                            )
                            setSelectedElement({
                              ...selectedElement,
                              fontWeight: newValue
                            })
                          }}
                        >
                          <MenuItem value="normal">Normal</MenuItem>
                          <MenuItem value="bold">Bold</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </>
              )}
            </Box>
          ) : (
            <Box sx={{ p: 2 }}>
              <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                Select an element to edit its properties
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  )
}

export default LabelDesignerDragDrop
